import { memo, useContext } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';

import { useLanguage } from 'assets/languages';
import Button from 'components/button';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import CampaignFormFieldGroup from '../../../../../campaignFormFieldGroup/campaignFormFieldGroup';
import { CampaignFormStepThreeContext } from '../../../../campaignFormStepThree.context';
import TextInputField from 'components/textInput';
import Select, { SELECT_ON_CHANGE_MODES } from 'components/select';

const ManagerFieldGroup = () => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };
  const { brand, campaignFormMethods } = useContext(CampaignFormContext);
  const { campaignManagerFormMethods, countryCodeOptions } = useContext(CampaignFormStepThreeContext);

  const campaignManagersFormFieldArray = useFieldArray({
    control: campaignManagerFormMethods.control,
    name: 'managers',
    keyName: '_reactHookFormId',
  });

  const inspectionFeedbackDueDt = useWatch({
    control: campaignFormMethods.control,
    name: 'inspectionFeedbackDueDt',
  });

  return (
    <CampaignFormFieldGroup
      wrapperClassName="!gap-[28px] !pb-0"
      label={brand.isWholesale ? languageTexts.create_step3_b_wholesale_title : languageTexts.create_step3_b_title}
      labelClassName="!text-2xl"
      description={languageTexts.create_step3_b_desc}
      descriptionClassName="!text-sm"
    >
      {(inspectionFeedbackDueDt != null) && (
        <p className="mb-[16px] text-sm text-[#FF0000]">{languageTexts.create_step3_b_notice}</p>
      )}

      <div className="flex flex-col gap-[30px]">
        {campaignManagersFormFieldArray.fields.map((_field, index) => (
          <div key={`campaign-managers-form-field-${index}`}>
            {/* 담당자 삭제 버튼 */}
            {index > 0 && (
              <div className="flex justify-end">
                <Button
                  className="max-w-[32px] h-[32px] bg-black"
                  icon={{
                    icon: (
                      <img
                        className="w-[32px] h-[32px] brightness-0 invert"
                        src={'/assets/icons/close.svg'}
                        alt="담당자 삭제"
                      />
                    ),
                    position: 'centerRight',
                  }}
                  onClick={() => campaignManagersFormFieldArray.remove(index)}
                />
              </div>
            )}

            <div className="flex flex-col gap-[30px] pb-[30px] border-b-[1px] border-[#E8E8E8]">
              {/* 담당자명 */}
              <CampaignFormFieldGroup.FormField
                wrapperClassName="!gap-[20px]"
                label={
                  brand.isWholesale
                    ? languageTexts.create_step3_a_wholesale_manager_name
                    : languageTexts.create_step3_a_manager_name
                }
                labelClassName="!text-[15px] !font-normal"
              >
                <TextInputField
                  bordered
                  placeholder={
                    brand.isWholesale
                      ? languageTexts.create_step3_a_wholesale_manager_name_placeholder
                      : languageTexts.create_step3_a_manager_name_placeholder
                  }
                  register={campaignManagerFormMethods.register(`managers.${index}.name`, { required: true })}
                />
              </CampaignFormFieldGroup.FormField>

              {/* 직책 */}
              <CampaignFormFieldGroup.FormField
                wrapperClassName="!gap-[20px]"
                label={languageTexts.create_step3_a_manager_position}
                labelClassName="!text-[15px] !font-normal"
              >
                <TextInputField
                  type="text"
                  bordered
                  placeholder={languageTexts.create_step3_a_manager_position_placeholder}
                  register={campaignManagerFormMethods.register(`managers.${index}.position`, {
                    required: true,
                  })}
                />
              </CampaignFormFieldGroup.FormField>

              {/* 국가 코드 */}
              <CampaignFormFieldGroup.FormField
                wrapperClassName="!gap-[20px]"
                label={languageTexts.contact_country_code}
                labelClassName="!text-[15px] !font-normal"
              >
                <Controller
                  control={campaignManagerFormMethods.control}
                  name={`managers.${index}.countryCode`}
                  render={({ field }) => (
                    <Select
                      bordered
                      onChangeMode={SELECT_ON_CHANGE_MODES.VALUE}
                      options={countryCodeOptions}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </CampaignFormFieldGroup.FormField>

              {/* 휴대폰 */}
              <CampaignFormFieldGroup.FormField
                wrapperClassName="!gap-[20px]"
                label={
                  brand.isWholesale
                    ? languageTexts.create_step3_a_wholesale_manager_phone
                    : languageTexts.create_step3_a_manager_phone
                }
                labelClassName="!text-[15px] !font-normal"
              >
                <TextInputField
                  type="tel"
                  numbersOnly
                  bordered
                  placeholder={languageTexts.create_step3_a_manager_phone_placeholder}
                  register={campaignManagerFormMethods.register(`managers.${index}.mobile`, {
                    required: true,
                    pattern: /^0[0-9]$/,
                  })}
                />
              </CampaignFormFieldGroup.FormField>
            </div>
          </div>
        ))}

        {/* 담당자 추가 버튼 */}
        <Button
          textStyle={{ fontSize: '16px' }}
          text={languageTexts.create_step3_b_add_manager}
          onClick={() =>
            campaignManagersFormFieldArray.append({
              name: '',
              countryCode: '',
              mobile: '',
              position: '',
            })
          }
        />
      </div>
    </CampaignFormFieldGroup>
  );
};

export default memo(ManagerFieldGroup);
